import React, { useState, useEffect } from "react";
import api from "../../services/Api";
import { formatDate } from "../../utils/Date";
import { CiEdit } from "react-icons/ci";
import { MdDeleteForever } from "react-icons/md";
import Title from "../Title";
import { BeatLoader } from "react-spinners";

const AdminDashboard = ({
  onCreateAdmin,
  onEditProfile,
  onEditAdmin,
  onDeleteAdmin,
}) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const response = await api.get("/admin");
        if (response.data && response.data.admins) {
          setAdmins(response.data.admins);
        }
      } catch (err) {
        setError(err.message || "Failed to fetch admin data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAdmins();
  }, []);

  if (loading) {
    return (
      <div className="w-full p-4 pl-64 h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} />
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div className="p-4 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi">
        <div className="px-4 py-3 text-red-700 bg-red-100 border border-red-400 rounded">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi">
      <Title title="Admin Dashboard" />
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
        {/* My Profile Card */}
        <div className="p-6 bg-white shadow-sm rounded-xl">
          <h2 className="mb-4 text-base font-semibold">My Profile</h2>
          <p className="text-sm text-gray-600 mb-2">
            Manage your admin profile settings and credentials.
          </p>
          <button
            onClick={onEditProfile}
            className="mt-4 bg-gray-100 text-gray-800 px-4 py-2 rounded text-[10px] hover:bg-gray-200"
          >
            Edit Profile
          </button>
        </div>

        {/* Create Admin Card */}
        <div className="p-6 bg-white shadow-sm rounded-xl">
          <h2 className="mb-4 text-base font-semibold">Create New Admin</h2>
          <p className="text-sm text-gray-600 mb-2">
            Add a new administrator to the system.
          </p>
          <button
            onClick={onCreateAdmin}
            className="mt-4 bg-black text-white px-4 py-2 rounded text-[10px] hover:bg-gray-800"
          >
            Create Admin
          </button>
        </div>
      </div>

      {/* Admin Table */}
      <div className="p-6 bg-white shadow-sm rounded-xl">
        <h2 className="mb-2 text-base font-bold">Admin List</h2>
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead>
              <tr className="text-left bg-[#ECECEC] text-gray-500 font-semibold text-sm ">
                <th className="p-2">No</th>
                <th className="p-2">Name</th>
                <th className="p-2">Email</th>
                <th className="p-2">Role</th>
                <th className="p-2">Nationality</th>
                <th className="p-2">Created At</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin, index) => (
                <tr key={admin._id} className="border-b font-medium text-sm text-[#191B1C] hover:bg-gray-50">
                  <td className="p-2 text-[#191B1C]">
                    {index + 1 || "-"}
                  </td>
                  <td>
                    {admin.fullName || "-"}
                  </td>
                  <td>
                    {admin.email || "-"}
                  </td>
                  <td>
                    {admin.role || admin.accessRole || "-"}
                  </td>
                  <td>
                    {admin.nationality || "-"}
                  </td>
                  <td>
                    {formatDate(admin.createdAt) || "-"}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          if (typeof onEditAdmin === "function") {
                            onEditAdmin(admin._id);
                          } else {
                            console.error("onEditAdmin is not defined");
                          }
                        }}
                        className="bg-gray-100 px-3 py-1 rounded text-[10px] hover:bg-gray-200"
                      >
                        <CiEdit className="w-4 h-4 text-gray-800" />
                      </button>
                      <button
                        onClick={() => {
                          if (typeof onDeleteAdmin === "function") {
                            onDeleteAdmin(admin._id);
                          } else {
                            console.error("onDeleteAdmin is not defined");
                          }
                        }}
                        className="bg-red-100 px-3 py-1 rounded text-[10px] hover:bg-red-200"
                      >
                        <MdDeleteForever className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
